/**
 * Navigation UI Manager - Handles navigation chips, campus display, and materials counting
 * Part of the SOLID Course Materials System
 *
 * @author Course Materials Team
 * @version 1.0.0
 */

class NavigationUIManager {
  /** Course sections currently visible for the active department chip */
  static get VISIBLE_COURSE_SECTION_SELECTOR() {
    return '.course-section:not(.cmrp-dept-filtered-out):not([style*="display: none"]):not([style*="display:none"])';
  }

  /**
   * @param {Object} config - Configuration object
   * @param {Array<any>} config.selectedFilters - Selected filters array
   * @param {Array<any>} config.products - Products array
   */
  constructor(config = /** @type {any} */ ({})) {
    /** @type {Array<any>} */
    this.selectedFilters = config.selectedFilters || [];
    /** @type {Array<any>} */
    this.products = config.products || [];

    // Material type filter state
    this.currentMaterialTypeFilter = 'all';
    this.dropdownOpen = false;
    // Division/department chip currently applied (`all` or a group key)
    this.activeScrollGroup = 'all';

    // Bind methods to preserve context
    this.initializeDepartmentFilters =
      this.initializeDepartmentFilters.bind(this);
    this.generateDepartmentChips = this.generateDepartmentChips.bind(this);
    this.filterProductsByDepartment =
      this.filterProductsByDepartment.bind(this);
    this.scrollToScrollGroup = this.scrollToScrollGroup.bind(this);
    this.applyScrollGroupFilter = this.applyScrollGroupFilter.bind(this);
    this.collectScrollChipGroups = this.collectScrollChipGroups.bind(this);
    this.updateMaterialsCount = this.updateMaterialsCount.bind(this);
    this.updateCampusTermDisplay = this.updateCampusTermDisplay.bind(this);
    this.hideNavigationPanels = this.hideNavigationPanels.bind(this);
    this.refreshNavigation = this.refreshNavigation.bind(this);

    // Material type filter methods
    this.initializeMaterialTypeDropdown =
      this.initializeMaterialTypeDropdown.bind(this);
    this.toggleMaterialTypeDropdown =
      this.toggleMaterialTypeDropdown.bind(this);
    this.filterProductsByMaterialType =
      this.filterProductsByMaterialType.bind(this);
    this.closeMaterialTypeDropdown = this.closeMaterialTypeDropdown.bind(this);
  }

  /**
   * Initializes department filters by generating chips
   * @returns {void}
   */
  initializeDepartmentFilters() {
    this.generateDepartmentChips();
    this.initializeMaterialTypeDropdown();
  }

  /**
   * Extracts unique material types from available products
   * @returns {Array<{value: string, label: string}>} Array of material type options
   */
  extractMaterialTypesFromProducts() {
    const materialTypes = new Set();

    // Focus ONLY on visible materials in DOM - this is what matters to the user
    const visibleMaterialItems = document.querySelectorAll(
      '.material-item:not([style*="none"])'
    );

    if (visibleMaterialItems.length === 0) {
      // Fallback: if no materials visible yet, check all products data
      if (window.courseData.products_Materials
       && window.courseData.products_Materials.length > 0) {
        window.courseData.products_Materials.forEach((product, index) => {
          if (product.material_type && product.material_type.trim()) {
            materialTypes.add(product.material_type.trim());
          }
        });
      }
    } else {
      // Primary approach: Only extract from currently visible materials
      visibleMaterialItems.forEach((materialItem, index) => {
        /** @type {HTMLElement} */
        const element = /** @type {HTMLElement} */ (materialItem);
        const product = this.getProductFromMaterialItem(element);

        if (product) {
          const materialType = /** @type {any} */ (product).material_type;
          if (materialType && materialType.trim()) {
            materialTypes.add(materialType.trim());
          }
        }
      });
    }

    // Convert to array and sort with preferred order
    const typeArray = Array.from(materialTypes);
    const preferredOrder = ['REQUIRED', 'RECOMMENDED', 'CHOICE'];

    const sortedTypes = typeArray.sort((a, b) => {
      const aIndex = preferredOrder.indexOf(a.toUpperCase());
      const bIndex = preferredOrder.indexOf(b.toUpperCase());

      // If both are in preferred order, sort by their index
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }

      // If only one is in preferred order, prioritize it
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;

      // If neither is in preferred order, sort alphabetically
      return a.localeCompare(b);
    });

    // Convert to option objects
    const options = sortedTypes.map(type => ({
      value: type.toLowerCase(),
      label: this.formatMaterialTypeLabel(type),
    }));

    return options;
  }

  /**
   * Formats material type for display
   * @param {string} type - Raw material type
   * @returns {string} Formatted label
   */
  formatMaterialTypeLabel(type) {
    // Convert to title case for better display
    return type
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * Initializes the material type dropdown functionality
   * @returns {void}
   */
  initializeMaterialTypeDropdown() {
    const dropdownBtn = document.getElementById('all-materials-btn');
    const dropdownContainer = document.querySelector('.all-materials-dropdown');

    if (!dropdownBtn || !dropdownContainer) {
      return;
    }

    // Remove existing dropdown menu if present
    const existingDropdown = dropdownContainer.querySelector(
      '.material-type-dropdown-menu'
    );
    if (existingDropdown) {
      existingDropdown.remove();
    }

    // Create dropdown menu
    const dropdownMenu = document.createElement('div');
    dropdownMenu.className = 'material-type-dropdown-menu';
    dropdownMenu.style.display = 'none';

    // Extract unique material types from products
    const materialTypes = this.extractMaterialTypesFromProducts();

    const options = [
      { value: 'all', label: 'All Materials' },
      ...materialTypes,
    ];

    options.forEach((option, index) => {
      const menuItem = document.createElement('div');
      menuItem.className = 'dropdown-menu-item';
      menuItem.textContent = option.label;
      menuItem.onclick = e => {
        e.preventDefault();
        e.stopPropagation();

        this.filterProductsByMaterialType(option.value, option.label);
        this.closeMaterialTypeDropdown();
      };
      dropdownMenu.appendChild(menuItem);
    });

    dropdownContainer.appendChild(dropdownMenu);

    // Add click event to button
    dropdownBtn.onclick = e => {
      e.stopPropagation();
      this.toggleMaterialTypeDropdown();
    };

    // Close dropdown when clicking outside
    document.addEventListener('click', e => {
      if (!dropdownContainer.contains(/** @type {Node} */ (e.target))) {
        this.closeMaterialTypeDropdown();
      }
    });
  }

  /**
   * Toggles the material type dropdown visibility
   * @returns {void}
   */
  toggleMaterialTypeDropdown() {
    const dropdownMenu = document.querySelector('.material-type-dropdown-menu');
    if (!dropdownMenu) return;

    this.dropdownOpen = !this.dropdownOpen;
    dropdownMenu.style.display = this.dropdownOpen ? 'block' : 'none';

    // Update chevron icon direction
    const chevronIcon = document.querySelector(
      '#all-materials-btn .chevron-down-icon'
    );
    if (chevronIcon) {
      chevronIcon.style.transform = this.dropdownOpen
        ? 'rotate(180deg)'
        : 'rotate(0deg)';
    }
  }

  /**
   * Closes the material type dropdown
   * @returns {void}
   */
  closeMaterialTypeDropdown() {
    const dropdownMenu = document.querySelector('.material-type-dropdown-menu');
    if (!dropdownMenu) return;

    this.dropdownOpen = false;
    dropdownMenu.style.display = 'none';

    // Reset chevron icon
    const chevronIcon = document.querySelector(
      '#all-materials-btn .chevron-down-icon'
    );
    if (chevronIcon) {
      chevronIcon.style.transform = 'rotate(0deg)';
    }
  }

  /**
   * Filters products by material type
   * @param {string} materialType - Material type to filter by ('all', 'required', 'recommended', 'choice')
   * @param {string} label - Display label for the filter
   * @returns {void}
   */
  filterProductsByMaterialType(materialType, label) {
    this.currentMaterialTypeFilter = materialType;

    // Update button text
    const buttonText = document.getElementById('all-materials-text');
    if (buttonText) {
      buttonText.textContent = label;
    }

    // Apply material type filter to all materials
    const allMaterialItems = document.querySelectorAll('.material-item');

    allMaterialItems.forEach((materialItem, index) => {
      /** @type {HTMLElement} */
      const element = /** @type {HTMLElement} */ (materialItem);
      const shouldShow = this.shouldShowMaterialByType(element, materialType);

      if (shouldShow) {
        element.classList.remove('filtering-out');
        element.classList.add('filtering-in');
        // Reset any inline display: none from previous filtering
        if (element.style.display === 'none') {
          element.style.display = '';
        }
      } else {
        element.classList.remove('filtering-in');
        element.classList.add('filtering-out');
        // Hide after animation completes
        setTimeout(() => {
          if (element.classList.contains('filtering-out')) {
            element.style.display = 'none';
          }
        }, 300); // Match CSS transition duration
      }
    });

    // Update course sections visibility
    this.updateCourseSectionsVisibility();

    // Update materials count
    this.updateMaterialsCount();

    // Dispatch custom event
    this.dispatchNavigationChangeEvent('materialTypeFilter', {
      materialType,
      label,
    });
  }

  /**
   * Determines if a material item should be shown based on material type filter
   * @param {HTMLElement} materialItem - Material item element
   * @param {string} materialType - Current filter ('all', 'required', 'recommended', 'choice')
   * @returns {boolean} Whether the item should be shown
   */
  shouldShowMaterialByType(materialItem, materialType) {
    if (materialType === 'all') {
      return true;
    }

    // Get the product data from the material item
    const product = this.getProductFromMaterialItem(materialItem);
    if (!product) {
      return false;
    }

    /** @type {any} */
    const productData = product;

    // Compare with actual material_type from product data
    if (!productData.material_type || !productData.material_type.trim()) {
      return false; // No material type defined
    }

    const productMaterialType = productData.material_type.trim().toLowerCase();
    const filterType = materialType.toLowerCase();
    const matches = productMaterialType === filterType;

    return matches;
  }

  /**
   * Gets the product data associated with a material item DOM element
   * @param {HTMLElement} materialItem - Material item element
   * @returns {Object|null} Product data or null if not found
   */
  getProductFromMaterialItem(materialItem) {
    if (!window.courseData.products_Materials || window.courseData.products_Materials.length === 0) return null;

    // Try to find product by title
    const titleElement = materialItem.querySelector('.material-title');
    if (!titleElement) return null;

    const productTitle = titleElement.textContent.trim();

    // Find the product in our products array by matching title
    let matchedProduct = window.courseData.products_Materials.find(
      product => product.title && product.title.trim() === productTitle
    );

    // If no exact match, try partial matching (fallback)
    if (!matchedProduct) {
      matchedProduct = window.courseData.products_Materials.find(product => {
        if (!product.title) return false;
        const normalizedProductTitle = product.title.trim().toLowerCase();
        const normalizedSearchTitle = productTitle.toLowerCase();
        return (
          normalizedProductTitle.includes(normalizedSearchTitle) ||
          normalizedSearchTitle.includes(normalizedProductTitle)
        );
      });
    }

    return matchedProduct || null;
  }

  /**
   * True when a section has Suggested / Choice / Bundle UI or an empty-state
   * message (none of which use `.material-item`).
   * @param {Element} section
   * @returns {boolean}
   */
  sectionHasNonMaterialItemContent(section) {
    return Boolean(
      section.querySelector(
        [
          'suggested-materials-component .suggested-materials-section',
          'choice-products-component',
          'bundle-products-component',
          '.no-materials-message',
          '.adoption-message-content',
        ].join(', ')
      )
    );
  }

  /**
   * Updates course sections visibility based on visible materials
   * @returns {void}
   */
  updateCourseSectionsVisibility() {
    document.querySelectorAll('.course-section').forEach(section => {
      const materialItems = section.querySelectorAll('.material-item');
      const visibleMaterials = section.querySelectorAll(
        '.material-item:not([style*="none"]):not(.filtering-out)'
      );
      // Prefer the regular card; adoption cards are handled separately below.
      const courseCard =
        section.querySelector('.course-card:not(.adoption-card)') ||
        section.querySelector('.course-card');
      const adoptionCard = section.querySelector('.course-card.adoption-card');
      const hasVisibleContent =
        visibleMaterials.length > 0 ||
        this.sectionHasNonMaterialItemContent(section);

      if (!hasVisibleContent) {
        // Hide the course card (e.g. all material-items filtered out)
        if (courseCard && courseCard !== adoptionCard) {
          courseCard.style.display = 'none';
          courseCard.style.visibility = 'hidden';
        }
        if (adoptionCard) {
          if (adoptionCard.style.display === 'none') {
            adoptionCard.style.display = '';
          }
          if (adoptionCard.style.visibility === 'hidden') {
            adoptionCard.style.visibility = 'visible';
          }
        }
      } else {
        // Show the course card - reset any previous inline styles
        if (courseCard) {
          if (courseCard.style.display === 'none') {
            courseCard.style.display = '';
          }
          if (courseCard.style.visibility === 'hidden') {
            courseCard.style.visibility = 'visible';
          }
        }
      }
    });
    this.updateCampusMaterialsGroupsVisibility();
  }

  /**
   * Generates filter chips for unique divisions (or departments when no division).
   * Chips filter the visible course list to the selected group; re-click toggles
   * back to All. The chips row only renders when more than one group is present
   * (2+ divisions when division grouping is active, or 2+ departments otherwise).
   * @returns {void}
   */
  /**
   * Department labels for chips, following on-screen campus group order
   * (materials-first), then course-section order within each campus.
   * Unique by lowercase name; first spelling wins.
   * Single-campus pages have no group wrappers — keeps selectedFilters order.
   * @returns {string[]}
   */
  getDepartmentLabelsInCampusDisplayOrder() {
    /** @type {string[]} */
    const departments = [];
    const seen = new Set();
    const addDept = raw => {
      const dept = String(raw || '').trim();
      if (!dept) return;
      const key = dept.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      departments.push(dept);
    };

    const labelByKey = new Map();
    for (const filter of this.selectedFilters || []) {
      const dept = String(filter?.selectedDepartment || '').trim();
      if (!dept) continue;
      const key = dept.toLowerCase();
      if (!labelByKey.has(key)) labelByKey.set(key, dept);
    }

    const productsContainer = document.getElementById('filtered-products');
    const groupEls = Array.from(productsContainer?.children || []).filter(
      el => el.classList?.contains('campus-materials-group'),
    );

    if (groupEls.length > 0) {
      for (const group of groupEls) {
        group.querySelectorAll('.course-section').forEach(section => {
          const fromData = String(
            section.getAttribute('data-department-filter') || '',
          )
            .trim()
            .toLowerCase();
          if (!fromData) return;
          addDept(labelByKey.get(fromData) || fromData);
        });
      }
      for (const filter of this.selectedFilters || []) {
        addDept(filter?.selectedDepartment);
      }
      return departments;
    }

    for (const filter of this.selectedFilters || []) {
      addDept(filter?.selectedDepartment);
    }
    return departments;
  }

  async generateDepartmentChips() {
    try {
      const chipsContainer = document.getElementById('department-filter-chips');
      const navPanel = document.getElementById('right-panel-nav');
      const listHeader = document.getElementById('cmrp-list-header');
      if (!chipsContainer || !navPanel) {
        return;
      }

      const groups = this.collectScrollChipGroups();
      const hasVisibleProducts =
        document.querySelectorAll('.course-section .material-item').length > 0 ||
        document.querySelectorAll('.course-section .adoption-status-section')
          .length > 0 ||
        document.querySelectorAll(
          '.course-section suggested-materials-component .suggested-materials-section'
        ).length > 0 ||
        document.querySelectorAll('.course-section choice-products-component')
          .length > 0 ||
        document.querySelectorAll('.course-section bundle-products-component')
          .length > 0 ||
        document.querySelectorAll('.course-section .no-materials-message')
          .length > 0;
      const hasResults =
        this.selectedFilters.length > 0 || hasVisibleProducts;

      if (listHeader) {
        if (hasResults) {
          listHeader.hidden = false;
        } else {
          listHeader.hidden = true;
        }
      }

      // HEOA: keep title row; never show division/department filter chips
      const isHeoa =
        window.__follettDiscoverViewFlow === true ||
        window.ProductSystem?.isFollettDiscoverViewPage?.() === true ||
        listHeader?.classList.contains('cmrp-list-header--heoa');

      if (isHeoa) {
        navPanel.style.display = 'none';
        navPanel.hidden = true;
        chipsContainer.innerHTML = '';
        return;
      }

      const previousKey = String(
        this.activeScrollGroup ||
          document
            .querySelector('.department-chip.active')
            ?.getAttribute('data-scroll-group') ||
          'all'
      )
        .toLowerCase()
        .trim() || 'all';

      // Only show chip row when there are multiple groups to filter between
      if (hasResults && groups.length > 1) {
        navPanel.style.display = 'flex';
        navPanel.hidden = false;

        this.updateMobileCourseHeader();

        chipsContainer.innerHTML = '';
        const allBtn = document.createElement('button');
        allBtn.type = 'button';
        allBtn.className = 'department-chip';
        allBtn.setAttribute('data-scroll-group', 'all');
        allBtn.setAttribute('aria-pressed', 'false');
        allBtn.textContent = 'All';
        allBtn.onclick = () => this.filterProductsByDepartment('all');
        chipsContainer.appendChild(allBtn);

        groups.forEach(({ key, label }) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'department-chip';
          button.setAttribute('data-scroll-group', key);
          button.setAttribute('aria-pressed', 'false');
          button.textContent =
            typeof window.decodeCmrpHtmlEntities === 'function'
              ? window.decodeCmrpHtmlEntities(label)
              : String(label ?? '').replace(/&amp;/gi, '&');
          button.onclick = () => this.filterProductsByDepartment(key);
          chipsContainer.appendChild(button);
        });

        // Keep the selected chip after a course-card rebuild when that group
        // still has remaining cards. Only fall back to All when the group is gone.
        const restoreKey = groups.some(({ key }) => key === previousKey)
          ? previousKey
          : 'all';
        this.applyScrollGroupFilter(restoreKey, { updateChips: true });
      } else {
        navPanel.style.display = 'none';
        navPanel.hidden = true;
        chipsContainer.innerHTML = '';
        this.applyScrollGroupFilter('all', { updateChips: false });
      }
    } catch (error) {
      console.warn('Error generating department chips:', error);
    }
  }

  /**
   * Unique division labels when any filter has a division; otherwise departments.
   * Falls back to DOM scroll-group attributes when filters are empty.
   * @returns {Array<{key: string, label: string}>}
   */
  collectScrollChipGroups() {
    /** @type {Map<string, string>} */
    const byKey = new Map();

    const anyDivision = (this.selectedFilters || []).some(
      /** @param {any} filter */ filter =>
        String(filter?.selectedDivision || '').trim()
    );

    (this.selectedFilters || []).forEach(
      /** @param {any} filter */ filter => {
        const raw = anyDivision
          ? String(filter?.selectedDivision || '').trim()
          : String(filter?.selectedDepartment || '').trim();
        if (!raw) return;
        const key = raw.toLowerCase();
        if (!byKey.has(key)) byKey.set(key, raw);
      }
    );

    if (byKey.size === 0) {
      document.querySelectorAll('.course-section[data-scroll-group]').forEach(
        /** @param {Element} section */ section => {
          const key = String(
            section.getAttribute('data-scroll-group') || ''
          ).trim();
          if (!key || key === 'all') return;
          if (byKey.has(key)) return;
          const title = section.querySelector('.department-name');
          const label = title?.textContent?.trim() || key;
          byKey.set(key, label);
        }
      );
    }

    return Array.from(byKey.entries()).map(([key, label]) => ({ key, label }));
  }

  /**
   * Filters the course list to a division/department chip group.
   * Re-clicking an already-selected (non-All) chip toggles back to All.
   * @param {string} groupKey
   * @returns {void}
   */
  scrollToScrollGroup(groupKey) {
    this.filterProductsByDepartment(groupKey);
  }

  /**
   * Filters visible `.course-section` rows to the selected division/department
   * group. Clicking an already-active non-All chip deselects it and shows All.
   * @param {string} department - Group key (`all`, division, or department)
   * @returns {void}
   */
  filterProductsByDepartment(department) {
    const requested = String(department || 'all').toLowerCase().trim() || 'all';
    const activeChip = document.querySelector('.department-chip.active');
    const activeKey = String(
      activeChip?.getAttribute('data-scroll-group') || 'all'
    )
      .toLowerCase()
      .trim();

    // Toggle-off: re-selecting the active chip returns to All
    const key =
      requested !== 'all' && activeKey === requested ? 'all' : requested;

    this.applyScrollGroupFilter(key, { updateChips: true });
    this.scrollResultsUnderPinnedHeader();
    this.dispatchNavigationChangeEvent('filter', { group: key });
  }

  /**
   * After a chip filter, bring `.campus-term-content` up to sit just below
   * the list header. Align to the sticky chrome slot (site header + list
   * header height), not the header's current viewport box — after a long
   * list collapses to a short one, the header and campus-term can both sit
   * off-screen together, so relative alignment would no-op.
   * Only scrolls up, so an unpinned header near the top of the page is not
   * dragged.
   * @returns {void}
   */
  scrollResultsUnderPinnedHeader() {
    const header = document.getElementById('cmrp-list-header');
    if (!(header instanceof HTMLElement)) return;

    const align = () => {
      const siteHeader =
        document.querySelector('.header-section') ||
        document.querySelector('#header-component');
      const stickyTop =
        siteHeader instanceof HTMLElement
          ? Math.ceil(siteHeader.getBoundingClientRect().bottom)
          : 0;

      const campusTermContent = this.getVisibleCampusTermContent();
      const scrollTarget =
        campusTermContent || document.getElementById('filtered-products');
      if (!(scrollTarget instanceof HTMLElement)) return;

      const topOffset = stickyTop + Math.ceil(header.offsetHeight);
      const delta = Math.ceil(scrollTarget.getBoundingClientRect().top) - topOffset;
      if (delta >= -1) return;

      window.scrollBy({ top: delta, behavior: 'auto' });
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        align();
        // Second pass after height collapse and sticky re-pin.
        requestAnimationFrame(align);
      });
    });
  }

  /**
   * Visible campus/term heading at the top of the results list.
   * Prefers the page-level bar; falls back to the first remaining group.
   * @returns {HTMLElement|null}
   */
  getVisibleCampusTermContent() {
    const pageBar = document.getElementById('campus-term-info');
    const pageContent = pageBar?.querySelector('.campus-term-content');
    if (
      pageBar instanceof HTMLElement &&
      pageContent instanceof HTMLElement &&
      pageBar.hidden !== true &&
      pageBar.style.display !== 'none' &&
      pageBar.getClientRects().length > 0
    ) {
      return pageContent;
    }

    const inlineContent = document.querySelector(
      '.campus-materials-group:not(.campus-materials-group--filtered-out) .campus-term-content'
    );
    return inlineContent instanceof HTMLElement ? inlineContent : null;
  }

  /**
   * Show/hide course sections for a scroll-group key and refresh material counts.
   * @param {string} groupKey
   * @param {{ updateChips?: boolean }} [options]
   * @returns {void}
   */
  applyScrollGroupFilter(groupKey, options = {}) {
    const key = String(groupKey || 'all').toLowerCase().trim() || 'all';
    this.activeScrollGroup = key;
    const updateChips = options.updateChips !== false;

    if (updateChips) {
      document.querySelectorAll('.department-chip').forEach(
        /** @param {Element} chip */ chip => {
          const isActive =
            (chip.getAttribute('data-scroll-group') || '') === key;
          chip.classList.toggle('active', isActive);
          chip.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        }
      );
    }

    // Prefer class toggle so material-type helpers using VISIBLE_COURSE_SECTION_SELECTOR
    // still see filtered-out sections correctly; keep inline styles for legacy selectors.
    document.querySelectorAll('.course-section').forEach(
      /** @param {Element} el */ el => {
        if (!(el instanceof HTMLElement)) return;
        const matches =
          key === 'all' || this.sectionMatchesScrollGroup(el, key);
        el.classList.toggle('cmrp-dept-filtered-out', !matches);
        if (matches) {
          el.style.display = 'flex';
          el.style.visibility = 'visible';
        } else {
          el.style.display = 'none';
          el.style.visibility = 'hidden';
        }
      }
    );

    this.applyCurrentMaterialTypeFilter();
    this.updateCampusMaterialsGroupsVisibility();
    this.updateMaterialsCount();
  }

  /**
   * Whether a course section belongs to the selected chip group.
   * @param {HTMLElement} section
   * @param {string} groupKey
   * @returns {boolean}
   */
  sectionMatchesScrollGroup(section, groupKey) {
    const key = String(groupKey || '')
      .toLowerCase()
      .trim();
    if (!key) return true;

    const scrollGroup = String(
      section.getAttribute('data-scroll-group') || ''
    )
      .toLowerCase()
      .trim();
    const division = String(
      section.getAttribute('data-division-filter') || ''
    )
      .toLowerCase()
      .trim();
    const department = String(
      section.getAttribute('data-department-filter') || ''
    )
      .toLowerCase()
      .trim();

    return scrollGroup === key || division === key || department === key;
  }

  /**
   * Shows/hides multi campus|term wrappers based on whether they still have
   * visible course sections after a department filter. Also syncs the top
   * campus|term bar to the first remaining visible group.
   * @returns {void}
   */
  updateCampusMaterialsGroupsVisibility() {
    const productsContainer = document.getElementById('filtered-products');
    if (!productsContainer) return;

    const groups = Array.from(productsContainer.children).filter(
      el => el.classList?.contains('campus-materials-group'),
    );
    if (groups.length === 0) return;

    /** @type {HTMLElement[]} */
    const visibleGroups = [];

    groups.forEach(group => {
      const hasVisibleSection = Boolean(
        group.querySelector(NavigationUIManager.VISIBLE_COURSE_SECTION_SELECTOR),
      );

      if (hasVisibleSection) {
        group.style.display = '';
        group.style.visibility = '';
        group.classList.remove('campus-materials-group--filtered-out');
        visibleGroups.push(group);
      } else {
        group.style.display = 'none';
        group.style.visibility = 'hidden';
        group.classList.add('campus-materials-group--filtered-out');
      }
    });

    // First visible group owns the top-bar title; hide its duplicate inline header.
    groups.forEach(group => {
      group.classList.remove('campus-materials-group--first');
      group.classList.remove('campus-materials-group--last-visible');
      group
        .querySelector('.campus-term-bar--inline')
        ?.classList.remove('campus-term-bar--inline-primary');
    });

    visibleGroups.forEach((group, index) => {
      const header = group.querySelector('.campus-term-bar--inline');
      if (index === 0) {
        group.classList.add('campus-materials-group--first');
        header?.classList.add('campus-term-bar--inline-primary');
      }
      if (index === visibleGroups.length - 1) {
        group.classList.add('campus-materials-group--last-visible');
      }
    });

    const campusDisplay = document.getElementById('selected-campus-display');
    const campusTermBar = document.getElementById('campus-term-info');
    if (!campusDisplay || !campusTermBar || visibleGroups.length === 0) {
      return;
    }

    const title =
      visibleGroups[0]
        .querySelector('.campus-term-title')
        ?.textContent?.trim() || '';
    if (title) {
      const group = visibleGroups[0];
      if (window.CourseMaterialsUtils?.setResultsCampusTermHeading) {
        CourseMaterialsUtils.setResultsCampusTermHeading(
          campusDisplay,
          group.getAttribute('data-campus') || '',
          group.getAttribute('data-term') || title,
          { forceCampus: visibleGroups.length > 1 },
        );
      } else {
        campusDisplay.textContent = title;
      }
      campusTermBar.style.display = 'flex';
    }

    campusTermBar.classList.toggle(
      'campus-term-bar--multi',
      visibleGroups.length > 1,
    );
  }

  /**
   * Applies the current material type filter to all materials
   * @returns {void}
   */
  applyCurrentMaterialTypeFilter() {
    const visibleSectionMaterials = `${NavigationUIManager.VISIBLE_COURSE_SECTION_SELECTOR} .material-item`;

    if (this.currentMaterialTypeFilter === 'all') {
      // Show all materials in visible sections with smooth transition
      document.querySelectorAll(visibleSectionMaterials).forEach(materialItem => {
        /** @type {HTMLElement} */
        const element = /** @type {HTMLElement} */ (materialItem);
        element.classList.remove('filtering-out');
        element.classList.add('filtering-in');
        // Reset any inline display: none from previous filtering
        if (element.style.display === 'none') {
          element.style.display = '';
        }
      });
    } else {
      // Apply current material type filter with smooth transitions
      document.querySelectorAll(visibleSectionMaterials).forEach(materialItem => {
        /** @type {HTMLElement} */
        const element = /** @type {HTMLElement} */ (materialItem);
        const shouldShow = this.shouldShowMaterialByType(
          element,
          this.currentMaterialTypeFilter
        );

        if (shouldShow) {
          element.classList.remove('filtering-out');
          element.classList.add('filtering-in');
          // Reset any inline display: none from previous filtering
          if (element.style.display === 'none') {
            element.style.display = '';
          }
        } else {
          element.classList.remove('filtering-in');
          element.classList.add('filtering-out');
          setTimeout(() => {
            if (element.classList.contains('filtering-out')) {
              element.style.display = 'none';
            }
          }, 300);
        }
      });
    }

    // Update course sections visibility after material filtering
    this.updateCourseSectionsVisibility();
  }

  /**
   * Sums required-material counts from visible course cards (.required-count).
   * Uses the same per-course totals as the course headers (includes choice_minimum).
   * @returns {number}
   */
  getVisibleRequiredMaterialsCount() {
    const requiredCountEls = document.querySelectorAll(
      '.course-section:not([style*="none"]) .course-card:not(.adoption-card) .required-count'
    );

    return Array.from(requiredCountEls).reduce((sum, el) => {
      const card = el.closest('.course-card');
      if (
        card &&
        (card.style.display === 'none' || card.style.visibility === 'hidden')
      ) {
        return sum;
      }
      const match = (el.textContent || '').match(/(\d+)/);
      return sum + (match ? parseInt(match[1], 10) : 0);
    }, 0);
  }

  /**
   * Updates the count of required materials in the campus-term header
   * @returns {void}
   */
  updateMaterialsCount() {
    try {
      const materialsCount = document.getElementById('materials-count');
      if (!materialsCount) {
        return;
      }

      const count = this.getVisibleRequiredMaterialsCount();
      const numberEl = materialsCount.querySelector('.materials-count__number');
      const materialsWord = materialsCount.querySelector(
        '.materials-count__label-line:last-child'
      );
      const hasRequired = count > 0;

      if (numberEl) {
        numberEl.textContent = hasRequired ? String(count) : '';
      }
      if (materialsWord) {
        materialsWord.textContent = count === 1 ? 'Material' : 'Materials';
      }

      materialsCount.hidden = !hasRequired;
      materialsCount.setAttribute('aria-hidden', hasRequired ? 'false' : 'true');
      if (hasRequired) {
        materialsCount.setAttribute(
          'aria-label',
          `${count} Required Material${count !== 1 ? 's' : ''}`
        );
      } else {
        materialsCount.removeAttribute('aria-label');
      }
    } catch (error) {
      console.warn('Error updating materials count:', error);
    }
  }

  /**
   * Updates the campus and term display in the header
   * @returns {void}
   */
  updateCampusTermDisplay() {
    try {
      const campusDisplay = document.getElementById('selected-campus-display');
      const campusTermBar = document.getElementById('campus-term-info');
      if (!campusDisplay || !campusTermBar) {
        return;
      }

      const campusTermLeft = campusTermBar.querySelector('.campus-term-left');

      // Show campus|term whenever the active filter includes them — do not wait for
      // products[] or DOM material items (those load async after campus/term selection).
      if (this.selectedFilters.length === 0) {
        if (window.CourseMaterialsUtils?.clearCampusTermHeading) {
          CourseMaterialsUtils.clearCampusTermHeading(campusDisplay);
        } else {
          campusDisplay.textContent = '';
        }
        campusTermBar.style.display = 'none';
        campusTermBar.classList.remove('campus-term-bar--multi');
        if (campusTermLeft) campusTermLeft.style.display = '';
        const listHeader = document.getElementById('cmrp-list-header');
        if (listHeader) listHeader.hidden = true;
        return;
      }

      const uniqueCampusTerms = [];
      const seen = new Set();
      for (const filter of this.selectedFilters) {
        const campus = String(filter?.selectedCampus || '').trim();
        const termId = String(
          filter?.selectedTermId || filter?.selectedTerm || '',
        ).trim();
        const key = `${campus.toLowerCase()}||${termId.toLowerCase()}`;
        if (!campus && !termId) continue;
        if (seen.has(key)) continue;
        seen.add(key);
        uniqueCampusTerms.push(filter);
      }

      // Multi campus/term: show the first *visible* campus|term in the top bar
      // beside Print List (materials-first DOM order). Do not pin selectedFilters[0]
      // (URL order can be a status-only campus). Later groups use inline headers.
      const productsContainer = document.getElementById('filtered-products');
      const groupEls = Array.from(productsContainer?.children || []).filter(
        el => el.classList?.contains('campus-materials-group'),
      );
      const multiGroupCount = groupEls.length;
      const multiFromDom =
        (productsContainer?.classList.contains('has-multi-campus-groups') ===
          true &&
          multiGroupCount > 1) ||
        multiGroupCount > 1;
      const isMultiCampusTerm =
        uniqueCampusTerms.length > 1 || multiFromDom;

      campusTermBar.classList.toggle(
        'campus-term-bar--multi',
        isMultiCampusTerm,
      );
      if (campusTermLeft) campusTermLeft.style.display = '';

      const firstVisibleGroup = groupEls.find(el => {
        const htmlEl = /** @type {HTMLElement} */ (el);
        if (
          htmlEl.style.display === 'none' ||
          htmlEl.style.visibility === 'hidden'
        ) {
          return false;
        }
        if (!String(htmlEl.getAttribute('data-campus') || '').trim()) {
          return false;
        }
        return Boolean(
          htmlEl.querySelector(
            NavigationUIManager.VISIBLE_COURSE_SECTION_SELECTOR,
          ),
        );
      });
      const firstVisibleTitle = firstVisibleGroup
        ?.querySelector('.campus-term-title')
        ?.textContent?.trim();

      const firstFilter = uniqueCampusTerms[0] || this.selectedFilters[0];
      const campus = firstFilter?.selectedCampus || '';
      const term = firstFilter?.selectedTerm || '';
      const termId = firstFilter?.selectedTermId || '';

      const termName = window.courseData?.ddcs_term?.find(t => t.term_id === termId)?.name || term;
      const headingCampus =
        firstVisibleGroup?.getAttribute('data-campus') || campus;
      const headingTerm =
        firstVisibleGroup?.getAttribute('data-term') || termName;
      if (firstVisibleTitle || (campus && term)) {
        if (window.CourseMaterialsUtils?.setResultsCampusTermHeading) {
          CourseMaterialsUtils.setResultsCampusTermHeading(
            campusDisplay,
            headingCampus,
            headingTerm,
            { forceCampus: uniqueCampusTerms.length > 1 },
          );
        } else if (firstVisibleTitle) {
          campusDisplay.textContent = firstVisibleTitle;
        } else {
          campusDisplay.textContent = window.CourseMaterialsUtils
            ? CourseMaterialsUtils.formatCampusTermTitle(
                campus,
                termName,
                window.courseData?.ddcs_campus_entity,
              )
            : `${campus} | ${termName}`;
        }
        campusTermBar.style.display = 'flex';
        const listHeader = document.getElementById('cmrp-list-header');
        if (listHeader) listHeader.hidden = false;
      } else {
        if (window.CourseMaterialsUtils?.clearCampusTermHeading) {
          CourseMaterialsUtils.clearCampusTermHeading(campusDisplay);
        } else {
          campusDisplay.textContent = '';
        }
        campusTermBar.style.display = 'none';
      }
    } catch (error) {
      console.warn('Error updating campus term display:', error);
    }
  }

  /**
   * Hides navigation panels (useful for empty states)
   * @returns {void}
   */
  hideNavigationPanels() {
    const navPanel = document.getElementById('right-panel-nav');
    const campusTermBar = document.getElementById('campus-term-info');
    const chipsContainer = document.getElementById('department-filter-chips');
    const listHeader = document.getElementById('cmrp-list-header');

    if (navPanel) {
      navPanel.style.display = 'none';
      navPanel.hidden = true;
    }
    if (listHeader) listHeader.hidden = true;
    if (campusTermBar) {
      campusTermBar.style.display = 'none';
      campusTermBar.classList.remove('campus-term-bar--multi');
      const left = campusTermBar.querySelector('.campus-term-left');
      if (left) left.style.display = '';
    }
    if (chipsContainer) chipsContainer.innerHTML = '';
  }

  /**
   * Updates the selected filters reference (for external updates)
   * @param {Array<any>} newSelectedFilters - New selected filters array
   * @returns {void}
   */
  updateSelectedFilters(newSelectedFilters) {
    this.selectedFilters = newSelectedFilters;
  }

  /**
   * Updates the products reference (for external updates)
   * @param {Array<any>} newProducts - New products array
   * @returns {void}
   */
  updateProducts(newProducts) {
    this.products = newProducts;
    // Re-initialize dropdown with new product data
    setTimeout(() => {
      this.initializeMaterialTypeDropdown();
    }, 100);
  }

  /**
   * Refreshes all navigation components
   * Convenience method that updates everything
   * @returns {void}
   */
  refreshNavigation() {
    this.generateDepartmentChips();
    // Re-initialize dropdown to pick up any new material types
    this.initializeMaterialTypeDropdown();
    this.updateCampusTermDisplay();
    this.updateMaterialsCount();
  }

  /**
   * Gets the current material type filter
   * @returns {string} Current material type filter
   */
  getCurrentMaterialTypeFilter() {
    return this.currentMaterialTypeFilter;
  }

  /**
   * Resets all filters to default state
   * @returns {void}
   */
  resetAllFilters() {
    // Reset department filter
    this.resetDepartmentFilter();

    // Clean up any filtering classes before reset
    this.cleanupFilteringClasses();

    // Reset material type filter
    this.currentMaterialTypeFilter = 'all';
    this.filterProductsByMaterialType('all', 'All Materials');
  }

  /**
   * Cleans up filtering CSS classes from all material items
   * @returns {void}
   */
  cleanupFilteringClasses() {
    document.querySelectorAll('.material-item').forEach(item => {
      item.classList.remove('filtering-in', 'filtering-out');
      // Reset any inline display: none from previous filtering
      if (item.style.display === 'none') {
        item.style.display = '';
      }
    });
  }

  /**
   * Initializes the navigation UI on DOM ready
   * @returns {void}
   */
  initializeOnDOMReady() {
    this.hideNavigationPanels();

    if (this.selectedFilters.length > 0) {
      this.initializeDepartmentFilters();
      this.updateCampusTermDisplay();
    }

    // Listen for materials loaded event to update dropdown options
    document.addEventListener('materialsLoaded', () => {
      setTimeout(() => {
        this.initializeMaterialTypeDropdown();
      }, 50);
    });

    document.addEventListener('cmrp:campusTermDataReady', () => {
      this.updateCampusTermDisplay();
    });
  }

  /**
   * Gets the currently active department filter
   * @returns {string|null} Active department or null
   */
  getActiveDepartment() {
    const activeChip = document.querySelector('.department-chip.active');
    return (
      activeChip?.getAttribute('data-scroll-group') ||
      activeChip?.getAttribute('data-department') ||
      this.activeScrollGroup ||
      null
    );
  }

  /**
   * Sets the active department filter
   * @param {string} department - Department to set as active
   * @returns {void}
   */
  setActiveDepartment(department) {
    this.filterProductsByDepartment(department);
  }

  /**
   * Gets statistics about visible products
   * @returns {Object} Statistics object with counts and visibility info
   */
  getNavigationStats() {
    const visibleProducts = document.querySelectorAll(
      `${NavigationUIManager.VISIBLE_COURSE_SECTION_SELECTOR} .material-item`
    );
    const totalProducts = document.querySelectorAll(
      '.course-section .material-item'
    );
    const activeDepartment = this.getActiveDepartment();

    return {
      visibleProductsCount: visibleProducts.length,
      totalProductsCount: totalProducts.length,
      activeDepartment,
      hasActiveFilters: this.selectedFilters.length > 0,
      isNavigationVisible:
        document.getElementById('right-panel-nav')?.style.display === 'flex',
    };
  }

  /**
   * Dispatches a custom event when navigation changes
   * @private
   * @param {string} type - Type of navigation change
   * @param {Object} data - Additional data about the change
   * @returns {void}
   */
  dispatchNavigationChangeEvent(type, data) {
    const event = new CustomEvent('navigation:changed', {
      detail: {
        type,
        data,
        stats: this.getNavigationStats(),
      },
    });
    document.dispatchEvent(event);
  }

  /**
   * Resets all filters to show all departments
   * @returns {void}
   */
  resetDepartmentFilter() {
    this.filterProductsByDepartment('all');
  }

  /**
   * Updates the mobile course header with current campus/term and materials count
   * @returns {void}
   */
  updateMobileCourseHeader() {
    try {
      const mobileTitle = document.getElementById('mobile-course-title');
      const mobileSubtitle = document.getElementById('mobile-course-subtitle');
      
      if (!mobileTitle || !mobileSubtitle) {
        return;
      }

      // Get campus-term information from multiple sources
      let campusTermText = '';

      const inlineCampusTitles = Array.from(
        document.querySelectorAll(
          '#filtered-products .campus-materials-group .campus-term-title',
        ),
      )
        .map(el => (el.textContent || '').trim())
        .filter(Boolean);
      if (inlineCampusTitles.length > 1) {
        campusTermText = inlineCampusTitles.join(' · ');
      }
      
      // Try to get from campus-term display first
      const campusTermDisplay = document.getElementById('selected-campus-display');
      if (!campusTermText && campusTermDisplay) {
        campusTermText = window.CourseMaterialsUtils?.getCampusTermHeadingText
          ? CourseMaterialsUtils.getCampusTermHeadingText(campusTermDisplay)
          : campusTermDisplay.textContent?.trim() || '';
      }
      if (!campusTermText) {
        // Try to get from selected-campus-term-text as fallback
        const selectedCampusTermText = document.getElementById('selected-campus-term-text');
        if (selectedCampusTermText && selectedCampusTermText.textContent && selectedCampusTermText.textContent.trim()) {
          campusTermText = selectedCampusTermText.textContent.trim();
        }
      }

      const totalRequiredCount = this.getVisibleRequiredMaterialsCount();

      // Update mobile header
      mobileTitle.textContent = campusTermText || 'Course Materials';
      mobileSubtitle.textContent = `(${totalRequiredCount} Required Material${
        totalRequiredCount !== 1 ? 's' : ''
      })`;
    } catch (error) {
      console.warn('Error updating mobile course header:', error);
    }
  }
}

// Export for use in other modules and global scope
window.NavigationUIManager = NavigationUIManager;

// Create namespace if it doesn't exist
if (!window.CourseMatSystem) {
  window.CourseMatSystem = {};
}
window.CourseMatSystem.NavigationUIManager = NavigationUIManager;
